import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ReactTimeout from 'react-timeout';
import MLIcon from '../MLIcon';
import s from './MLAlert.module.css';

function getAlertProps(alertType) {
  let alertClass;
  let alertIconType;
  switch (alertType) {
    case 'error':
      alertClass = s.error;
      alertIconType = 'not';
      break;
    case 'warning':
      alertClass = s.warning;
      alertIconType = 'alert_outline';
      break;
    case 'success':
      alertClass = s.success;
      alertIconType = 'circle_check_outline';
      break;
    case 'notification':
      alertClass = s.notification;
      alertIconType = 'help_outline';
      break;
    case 'sync':
      alertClass = s.sync;
      alertIconType = 'refresh';
      break;
    default:
      alertClass = s.success;
      alertIconType = 'circle_check_outline';
  }
  return { alertClass, alertIconType };
}

const navSelector = '[data-id="nav-container"]';
const forceUpdateEvents = [
  'webkitTransitionEnd',
  'otransitionend',
  'oTransitionEnd',
  'msTransitionEnd',
  'transitionend',
];

class MLAlert extends Component {
  static propTypes = {
    className: PropTypes.string,
    alertType: PropTypes.string,
    dismissTime: PropTypes.number, // milliseconds default 3000
    dismissible: PropTypes.bool,
    onDismiss: PropTypes.func, // triggered when dismissed.
    onRequestClose: PropTypes.func, // triggered when user requests to dismiss.
    alertPosition: PropTypes.object,
    text: PropTypes.string,
    linkEl: PropTypes.object, // aTag Element spected
    show: PropTypes.bool.isRequired,
    top: PropTypes.number, // distance from the top of the screen;
    setTimeout: PropTypes.func,
    ignoreNavContainer: PropTypes.bool, // if true, do not position self based on nav-container
  };

  static getDerivedStateFromProps(nextProps, prevState) {
    if (prevState.show !== nextProps.show) {
      return {
        showClass: nextProps.show ? s.shown : '',
        show: nextProps.show,
      };
    }
    return null;
  }

  constructor(props) {
    super(props);
    this.state = {
      show: this.props.show || false,
      showClass: this.props.show ? s.shown : '',
    };
  }

  componentDidMount() {
    const { dismissible, dismissTime } = this.props;
    if (!dismissible || (dismissible && dismissTime)) {
      this.props.setTimeout(this.dismiss, dismissTime || 3000);
    }
    this.addForceUpdateEvents();
  }

  componentWillUnmount() {
    this.removeForceUpdateEvents();
  }

  getNavContainer() {
    if (this.navContainer) {
      return this.navContainer;
    }
    this.navContainer = document.querySelector(navSelector);
    return this.navContainer;
  }

  addForceUpdateEvents() {
    const navContainer = this.getNavContainer();
    if (navContainer) {
      forceUpdateEvents.forEach((event) => {
        navContainer.addEventListener(event, this.myForceUpdate);
      });
    }
  }

  removeForceUpdateEvents() {
    const navContainer = this.getNavContainer();
    if (navContainer) {
      forceUpdateEvents.forEach((event) => {
        navContainer.removeEventListener(event, this.myForceUpdate);
      });
    }
  }

  myForceUpdate = () => { this.forceUpdate(); };

  dismiss() {
    const { onDismiss } = this.props;
    this.setState({ showClass: '' });
    if (onDismiss) {
      onDismiss();
    }
  }

  renderDismissEl() {
    return (
      <div className={s.dismissIcon}>
        <button
          onClick={(e) => {
            this.setState({ showClass: '' });
            if (this.props.onRequestClose) {
              this.props.onRequestClose(e);
            }
            if (this.props.onDismiss) {
              this.props.onDismiss();
            }
          }}
          data-test-id="alert_close_btn"
        >
          <MLIcon type="x" title="Close" />
        </button>
      </div>
    );
  }

  renderAlertInPlace() {
    const {
      className,
      alertType,
      dismissible,
      text,
      linkEl,
    } = this.props;

    const alertProps = getAlertProps(alertType);

    return (
      <div
        className={`${className} ${s.alertInPlace} ${alertProps.alertClass} ${this.state.showClass}`}
        data-test-id="alert"
      >
        <div className={s.alertInPlaceIcon}>
          <MLIcon type={alertProps.alertIconType} />
        </div>
        <span className={s.alertText}>{text}. { linkEl || null }</span>
        {dismissible && this.renderDismissEl()}
      </div>
    );
  }

  renderAlertWithNavContainer() {
    const {
      className,
      alertType,
      dismissible,
      text,
      linkEl,
      fixedPosition,
    } = this.props;
    const alertProps = getAlertProps(alertType);

    const navContainer = document.querySelector(navSelector);

    let width;
    let alertLeft;
    if (navContainer) {
      const navContainerWidth = navContainer.clientWidth;
      // eslint-disable-next-line radix
      alertLeft = parseInt(navContainerWidth + 10);
      width = `calc(100% - ${alertLeft + 10}px)`;
    }
    const styles = {
      top: fixedPosition ? '' : this.props.top || 78,
      left: alertLeft,
      width,
    };
    return (
      <div
        style={styles}
        className={`${className} ${s.alert} ${alertProps.alertClass} ${this.state.showClass}`}
        data-test-id="alert"
      >
        <div className={s.alertIcon}>
          <MLIcon type={alertProps.alertIconType} />
        </div>
        <span className={s.alertText}>{text}. { linkEl || null }</span>
        {dismissible && this.renderDismissEl()}
      </div>
    );
  }

  render() {
    const { ignoreNavContainer } = this.props;
    return (
      <div>
        {
          ignoreNavContainer
          ? this.renderAlertInPlace()
          : this.renderAlertWithNavContainer()
        }
      </div>
    );
  }
}

// eslint-disable-next-line new-cap
export default ReactTimeout(MLAlert);
